import without from 'lodash/without';
import { func, shape, string } from 'prop-types';
import React from 'react';
import { Button } from 'reactstrap';

import withConfirm from '../../../generic/with-confirm';
import Titled from '../../../layout/Titled';
import { contactListShape, historyShape } from '../../../prop-types';
import api from '../../../REST/api';
import DeleteButton from '../../../REST/DeleteButton';
import withApiRequest from '../../../REST/with-api-request';
import withResolved from '../../../REST/with-resolved';
import { COLORS } from '../../../utils/constants';
import createCSV from '../../../utils/create-csv';
import ListContacts from '../../ListContacts';
import CloneContactList from '../CloneContactList';
import ContactListForm from '../ContactListForm';
import ExportWithSegmentations from '../ExportWithSegmentations';
import QuickAddContactsToListModal from '../QuickAddContactsToListModal';

export const csvColumns = [
  { accessor: 'firstName', label: 'First Name' },
  { accessor: 'lastName', label: 'Last Name' },
  { accessor: 'title', label: 'Title' },
  { accessor: 'email', label: 'Email' },
  { accessor: 'mobile', label: 'Mobile' },
  { accessor: 'phone', label: 'Phone' },
  { accessor: 'fax', label: 'Fax' },
  { accessor: 'account.name', label: 'Account Name' },
  { accessor: row => `A-${row.account.id}`, label: 'Account Number' },
  { accessor: 'created', label: 'Created' },
  { accessor: 'createdBy', label: 'Created By' },
  { accessor: 'lastModified', label: 'Last Modified' },
  { accessor: 'lastModifiedBy', label: 'Last Modified By' },
];

async function dump(rows) {
  return createCSV('contacts', csvColumns, rows);
}

const ButtonWithConfirmation = withConfirm(
  'Are you sure you want to remove the selected contact(s) from the list?',
)(Button);

class ShowContactList extends React.Component {
  state = { model: this.props.model };

  static getDerivedStateFromProps(props, state) {
    if (state.model.id === props.model.id) {
      return null;
    }
    return { model: props.model };
  }

  handleChange = e => {
    const { name, value } = e.target;
    this.setState(state => ({
      model: {
        ...state.model,
        [name]: value,
      },
    }));
  };

  handleSecondarySave = () => {
    const {
      model: { name, description, id },
    } = this.state;
    this.props.onRequest({ id, data: { name, description } });
  };

  handleRemove(selectedContactIds) {
    const {
      model: { contactIds, id },
    } = this.state;
    const idsToRemove = selectedContactIds.map(cId => Number(cId));
    this.props
      .onRequest({
        id,
        data: {
          contactIds: without(contactIds, ...idsToRemove),
        },
      })
      .then(this.updateModel);
  }

  updateModel = updatedModel => {
    this.setState({ model: updatedModel });
  };

  render() {
    const { model } = this.state;
    const { history } = this.props;

    return (
      <Titled
        title="Contact list"
        actions={[
          <DeleteButton
            onDelete={api.destroyContactList}
            id={model.id}
            name={model.name}
            redirectTo="/contacts"
          />,
          <Button
            className="btn btn-secondary ml-2"
            onClick={this.handleSecondarySave}
          >
            Update
          </Button>,
          <span className="ml-2">
            <CloneContactList contactList={model} history={history} />
          </span>,
          <ExportWithSegmentations
            className="ml-2"
            disabled={model.contactIds.length === 0}
            contactListId={model.id}
          />,
        ]}
        sticky
      >
        <ContactListForm model={model} onChange={this.handleChange} />
        <div className="mb-5" />
        <ListContacts
          titleSize="sm"
          contactListId={model.id}
          showAddContactButton={false}
          key={model.contactIds.join('')}
          title={`Contacts in the list (${model.contactIds.length})`}
          onDownload={dump}
          selectListAction={({ disabled, selectedContactIds }) => (
            <>
              <ButtonWithConfirmation
                color={COLORS.DANGER}
                onClick={() => this.handleRemove(selectedContactIds)}
                disabled={disabled}
                className="mr-2"
              >
                Remove {selectedContactIds.length} contact(s) from list
              </ButtonWithConfirmation>
              <QuickAddContactsToListModal
                list={model}
                onSuccess={this.updateModel}
              />
            </>
          )}
        />
      </Titled>
    );
  }
}

ShowContactList.propTypes = {
  model: contactListShape.isRequired,
  onRequest: func.isRequired,
  history: historyShape.isRequired,
};

const ShowContactListWithApiRequest = withApiRequest({
  query: api.patchContactList,
  error: 'flash',
  successMessage: 'Contact list updated',
})(ShowContactList);

const ShowContactListWithData = withResolved({
  query: api.getContactList,
  deriveQueryArgs: props => ({
    id: Number.parseInt(props.match.params.id, 10),
  }),
  as: 'model',
})(ShowContactListWithApiRequest);

ShowContactListWithData.propTypes = {
  match: shape({ params: shape({ id: string.isRequired }).isRequired })
    .isRequired,
};

export default ShowContactListWithData;
